import ElakayImage from '../../../components/images/ElakayImage';
// /provider/trash -- Provider products deleted by their Provider. While a product is here every
// Seller copy (Dropshipping In) is unsellable; Restore re-activates only the copies the deletion
// switched off (never one a Seller turned off or trashed); Delete Permanently is refused while an
// order or Seller-owned stock still depends on it. Server: dropshippingInterne/services/providerTrashService.js.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { elakayAutoText as text } from "../../pageInside/dataElakay";
import { toImageUrl } from "../../../config";
import PageShell, { tokenOf } from "./PageShell";
import { listProviderTrash, restoreProviderProduct, deleteProviderProductPermanently } from "../services/providerTrashService";
import { PROVIDER_ROUTES } from "../providerWorkspace";
import "./connectedSellers.css";
import "./providerTrash.css";

const PAGE_SIZE = 20;

const Thumb = ({ src, alt }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  return src && !broken
    ? <ElakayImage preset="product-thumb" className="cs-thumb" src={toImageUrl(src)} alt={alt || ""} loading="lazy" onError={() => setBroken(true)} />
    : <span className="cs-thumb cs-thumb--empty" aria-hidden="true"><i className="fas fa-box" /></span>;
};

const syncLabel = (status) => (status === "synced"
  ? text("h_ptr_sync_done", "All Seller copies unavailable")
  : text("h_ptr_sync_pending", "Synchronization pending"));

const restoreMessage = (sync) => {
  if (!sync?.copies) return text("h_ptr_restored", "Product restored to My Products.");
  return text("h_ptr_restored_sync", "Product restored. {reactivated} Seller copy(ies) back on sale, {stayedOff} still need the Seller's attention.", { reactivated: sync.reactivated || 0, stayedOff: sync.stayedOff || 0 });
};

export default function ProviderTrash({ infoSource }) {
  const [page, setPage] = useState(1);
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [tick, setTick] = useState(0);
  const [state, setState] = useState({ data: null, loading: true, error: "" });
  const [busy, setBusy] = useState(null); // `${action}:${id}`
  const [confirmId, setConfirmId] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true, error: "" }));
    listProviderTrash(tokenOf(), { page, limit: PAGE_SIZE, q: search }).then((r) => {
      if (!live) return;
      if (r?.success === false || r?.error) setState({ data: null, loading: false, error: r.error || "Error" });
      else setState({ data: r, loading: false, error: "" });
    });
    return () => { live = false; };
  }, [page, search, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  const submit = (e) => { e.preventDefault(); setPage(1); setSearch(input.trim()); };

  const restore = async (item) => {
    setBusy(`restore:${item._id}`); setNotice(null);
    const r = await restoreProviderProduct(tokenOf(), item._id);
    setBusy(null);
    if (r?.success === false) return setNotice({ type: "error", text: r.error || text("h_ptr_restore_failed", "The product could not be restored.") });
    setNotice({ type: "success", text: restoreMessage(r.sync) });
    reload();
  };

  const destroy = async (item) => {
    setBusy(`delete:${item._id}`); setNotice(null);
    const r = await deleteProviderProductPermanently(tokenOf(), item._id);
    setBusy(null); setConfirmId(null);
    if (r?.success === false) {
      return setNotice({ type: "error", text: r.code === "PRODUCT_IN_USE"
        ? text("h_ptr_in_use", "This product still has orders in progress or Seller-owned stock. It cannot be deleted permanently yet.")
        : r.error || text("h_ptr_delete_failed", "The product could not be deleted.") });
    }
    setNotice({ type: "success", text: text("h_ptr_deleted", "Product deleted permanently. Seller copies stay unavailable and order history is kept.") });
    reload();
  };

  const data = state.data;
  const items = data?.items || [];
  return (
    <PageShell infoSource={infoSource} titleElakay="providerProducts" title={text("h_ptr_title", "Provider Trash")} icon="fa-trash-alt" className="cs-page ptr-page"
      crumb={text("h_ptr_title", "Provider Trash")}
      subtitle={text("h_ptr_sub", "Deleted products. They cannot be sold, and every Seller copy is unavailable until you restore them.")}
      actions={<Link className="ctr__btn" to={PROVIDER_ROUTES.products}>‹ {text("h_pvc_nav_products", "My Products")}</Link>}
      state={{ loading: state.loading && !data, error: state.error, reload }}>
      <form className="cs-toolbar" onSubmit={submit} role="search">
        <div className="cs-search"><i className="fas fa-search" aria-hidden="true" />
          <input type="search" aria-label={text("h_ptr_search", "Search deleted products")} placeholder={text("h_ptr_search", "Search deleted products")} value={input} onChange={(e) => setInput(e.target.value)} />
        </div>
        <button type="submit" className="ctr__btn ctr__btn--primary">{text("h_cs_search_btn", "Search")}</button>
      </form>
      {notice && <div className={`ptr-notice ptr-notice--${notice.type}`} role={notice.type === "error" ? "alert" : "status"}>{notice.text}</div>}
      {!items.length ? (
        <div className="ctr__empty"><i className={`fas ${search ? "fa-search" : "fa-trash-alt"}`} aria-hidden="true" />
          <p>{search ? text("h_ptr_no_match", "No deleted product matches your search.") : text("h_ptr_empty", "Your trash is empty.")}</p></div>
      ) : (
        <ul className="ptr-list" aria-busy={state.loading}>
          {items.map((item) => (
            <li className="ptr-item" key={item._id} data-testid="ptr-item">
              <Thumb src={item.image} alt={item.name} />
              <div className="ptr-item__main">
                <strong className="ptr-item__name">{item.name || "—"}</strong>
                <small>{text("h_ptr_product_id", "Product ID")}: {item.publicId || item._id}{item.sku ? ` · SKU ${item.sku}` : ""}</small>
                {item.shop && <small><i className="fas fa-store" aria-hidden="true" /> {item.shop}</small>}
              </div>
              <dl className="ptr-item__facts">
                <div><dt>{text("h_ptr_deleted_on", "Deleted on")}</dt><dd>{item.deletedAt ? new Date(item.deletedAt).toLocaleDateString() : "—"}</dd></div>
                <div><dt>{text("h_ptr_qty_before", "Quantity before deletion")}</dt><dd>{Number(item.quantityAtDeletion || 0).toLocaleString()}</dd></div>
                <div><dt>{text("h_ptr_linked_sellers", "Linked Sellers")}</dt><dd>{item.linkedSellers}</dd></div>
                <div><dt>{text("h_ptr_seller_shops", "Seller shops")}</dt><dd>{item.sellerShops}</dd></div>
                <div><dt>{text("h_ptr_sync", "Synchronization")}</dt><dd className={`ptr-sync ptr-sync--${item.syncStatus}`}>{syncLabel(item.syncStatus)}</dd></div>
              </dl>
              <div className="ptr-item__actions">
                <button type="button" className="ctr__btn ctr__btn--primary" disabled={Boolean(busy)} onClick={() => restore(item)} data-testid="ptr-restore">
                  <i className="fas fa-undo" aria-hidden="true" /> {busy === `restore:${item._id}` ? text("h_ptr_restoring", "Restoring…") : text("h_ptr_restore", "Restore Product")}
                </button>
                {confirmId === item._id ? (
                  <span className="ptr-confirm" role="group" aria-label={text("h_ptr_confirm", "Delete permanently?")}>
                    <span>{text("h_ptr_confirm", "Delete permanently?")}</span>
                    <button type="button" className="ctr__btn ctr__btn--danger" disabled={Boolean(busy)} onClick={() => destroy(item)} data-testid="ptr-confirm-delete">
                      {busy === `delete:${item._id}` ? text("h_ptr_deleting", "Deleting…") : text("h_ptr_yes_delete", "Yes, delete")}
                    </button>
                    <button type="button" className="ctr__btn" disabled={Boolean(busy)} onClick={() => setConfirmId(null)}>{text("h_ptr_cancel", "Cancel")}</button>
                  </span>
                ) : (
                  <button type="button" className="ctr__btn ctr__btn--danger" disabled={Boolean(busy)} onClick={() => setConfirmId(item._id)} data-testid="ptr-delete">
                    <i className="fas fa-trash" aria-hidden="true" /> {text("h_ptr_delete_permanently", "Delete Permanently")}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {data?.pages > 1 && (
        <nav className="cs-pager" aria-label={text("h_cs_pagination", "Pagination")}>
          <button type="button" className="ctr__btn" disabled={data.page <= 1} onClick={() => setPage(data.page - 1)}>‹ {text("h_cs_prev", "Previous")}</button>
          <span>{data.page} / {data.pages}</span>
          <button type="button" className="ctr__btn" disabled={data.page >= data.pages} onClick={() => setPage(data.page + 1)}>{text("h_cs_next", "Next")} ›</button>
        </nav>
      )}
    </PageShell>
  );
}
